import api from './api';

export type WindowDays = 30 | 60 | 90 | 180;

export interface TrendPoint {
  date: string;
  moves: number;
  rejections: number;
}

export interface SummaryResponse {
  total_moves: number;
  total_rejections: number;
  reject_rate: number;
  rejected_pay_total: number;
  trend: TrendPoint[];
  available_regions: string[];
}

export interface ByDriverRow {
  driver: string;
  driver_name: string;
  isMule?: boolean;
  terminated: boolean;
  rejects: number;
  accepts: number;
  total: number;
  reject_rate: number;
  top_reason: string;
  locations_rejected_at: number;
  rejected_pay_total: number;
}

export interface ByLocationRow {
  location_id: number;
  location_name: string;
  location_state: string;
  location_city: string;
  rejects: number;
  accepts: number;
  total: number;
  reject_rate: number;
  top_reason: string;
  distinct_drivers_rejecting: number;
  /** Average estimated driver pay across rejected legs at this location.
   *  $0 rows are excluded from the average; null when no qualifying legs. */
  avg_rejected_pay: number | null;
}

export interface ByReasonRow {
  reason: string;
  count: number;
  distinct_drivers: number;
  distinct_locations: number;
  rejected_pay_total: number;
  last_seen: string | null;
}

export interface HotSpotRejecter {
  driver: string;
  driver_name: string;
  isMule?: boolean;
  terminated: boolean;
  rejects: number;
  accepts: number;
  reject_rate: number;
}

export interface HotSpotRow {
  location_id: number;
  location_name: string;
  location_state: string;
  location_city: string;
  rejects: number;
  accepts: number;
  total: number;
  reject_rate: number;
  top_rejecters: HotSpotRejecter[];
}

export interface RejectionRow {
  dispatch_recno: number;
  pronumber: number | null;
  rejectdatetime: string | null;
  driver: string;
  driver_name: string;
  isMule?: boolean;
  terminated: boolean;
  origin_id: number;
  origin_name: string;
  destination_id: number;
  destination_name: string;
  rejectreason: string;
  rejectcomment: string;
  estimated_driver_pay: number | null;
}

export interface AnalyticsFilters {
  days: WindowDays;
  regions?: string[]; // dispatcher.revenueregion values
  driver?: string;
  location_id?: number;
  reason?: string;
}

const buildParams = (filters: AnalyticsFilters) => {
  const params: Record<string, string | number> = { days: filters.days };
  if (filters.regions && filters.regions.length > 0) {
    params.regions = filters.regions.join(',');
  }
  if (filters.driver) params.driver = filters.driver;
  if (filters.location_id != null) params.location_id = filters.location_id;
  if (filters.reason) params.reason = filters.reason;
  return params;
};

export const rejectionAnalyticsService = {
  summary: async (filters: AnalyticsFilters): Promise<SummaryResponse> => {
    const res = await api.get('/customers/rejection-analytics/summary', {
      params: buildParams(filters),
    });
    return res.data;
  },
  byDriver: async (filters: AnalyticsFilters): Promise<ByDriverRow[]> => {
    const res = await api.get('/customers/rejection-analytics/by-driver', {
      params: buildParams(filters),
    });
    return res.data;
  },
  byLocation: async (filters: AnalyticsFilters): Promise<ByLocationRow[]> => {
    const res = await api.get('/customers/rejection-analytics/by-location', {
      params: buildParams(filters),
    });
    return res.data;
  },
  byReason: async (filters: AnalyticsFilters): Promise<ByReasonRow[]> => {
    const res = await api.get('/customers/rejection-analytics/by-reason', {
      params: buildParams(filters),
    });
    return res.data;
  },
  rejections: async (filters: AnalyticsFilters): Promise<RejectionRow[]> => {
    const res = await api.get('/customers/rejection-analytics/rejections', {
      params: buildParams(filters),
    });
    return res.data;
  },
  hotSpots: async (filters: AnalyticsFilters): Promise<HotSpotRow[]> => {
    const res = await api.get('/customers/rejection-analytics/hot-spots', {
      params: buildParams(filters),
    });
    return res.data;
  },
};

export default rejectionAnalyticsService;
